<script setup>
import { computed, ref, nextTick, watch, inject, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { picoSearch } from '@scmmishra/pico-search';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import ToolSelectorDialog from 'dashboard/components-next/pilot/pageComponents/customTool/ToolSelectorDialog.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

// 从父组件注入 assistantId
const injectedAssistantId = inject('assistantId');
const assistantId = computed(
  () => injectedAssistantId?.value || props.assistant?.id
);

// Store getters - 获取已关联的工具
const assistantTools = useMapGetter('pilotAssistantCustomTools/getRecords');

// 确保是数组
const linkedTools = computed(() => assistantTools.value || []);
const isEmpty = computed(() => !linkedTools.value.length);

// 搜索
const searchQuery = ref('');

const filteredTools = computed(() => {
  const query = searchQuery.value.trim();
  const source = linkedTools.value;
  if (!query) return source;
  return picoSearch(source, query, ['title', 'description', 'slug']);
});

// 选择器弹窗
const selectorDialog = ref(null);

// 批量选择状态
const selectedToolIds = ref(new Set());
const isBatchRemoving = ref(false);
const hoveredToolId = ref(null);

// 空状态假数据
const emptyStateTools = [
  {
    id: 1,
    title: '订单查询',
    description: '根据订单ID查询订单详情',
    auth_type: 'api_key',
  },
  {
    id: 2,
    title: '物流追踪',
    description: '获取包裹实时物流信息',
    auth_type: 'bearer',
  },
  {
    id: 3,
    title: '用户信息',
    description: '查询用户账户信息',
    auth_type: 'basic',
  },
  {
    id: 4,
    title: '库存检查',
    description: '检查商品库存状态',
    auth_type: 'none',
  },
];

// 切换选中状态
const toggleSelection = toolId => {
  if (selectedToolIds.value.has(toolId)) {
    selectedToolIds.value.delete(toolId);
  } else {
    selectedToolIds.value.add(toolId);
  }
  selectedToolIds.value = new Set(selectedToolIds.value);
};

// 判断是否选中
const isSelected = toolId => selectedToolIds.value.has(toolId);

// 选中数量
const hasSelection = computed(() => selectedToolIds.value.size > 0);

// 全选/取消全选标签
const buildSelectedCountLabel = computed(() => {
  const count = linkedTools.value.length || 0;
  const isAllSelected = selectedToolIds.value.size === count && count > 0;
  return isAllSelected
    ? t('PILOT.ASSISTANTS.CONFIG.TOOLS.BULK_ACTION.UNSELECT_ALL', { count })
    : t('PILOT.ASSISTANTS.CONFIG.TOOLS.BULK_ACTION.SELECT_ALL', { count });
});

const selectedCountLabel = computed(() => {
  return t('PILOT.ASSISTANTS.CONFIG.TOOLS.BULK_ACTION.SELECTED', {
    count: selectedToolIds.value.size,
  });
});

// 获取认证类型标签
const getAuthTypeLabel = authType => {
  const typeMap = {
    none: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE'),
    bearer: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.BEARER'),
    basic: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.BASIC'),
    api_key: t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.API_KEY'),
  };
  return (
    typeMap[authType] ||
    authType ||
    t('PILOT.CUSTOM_TOOLS.FORM.AUTH_TYPES.NONE')
  );
};

// 打开选择器弹窗
const handleOpenSelector = () => {
  nextTick(() => selectorDialog.value.open());
};

// 移除单个工具
const removingId = ref(null);
const handleRemoveTool = async tool => {
  removingId.value = tool.id;
  try {
    await store.dispatch('pilotAssistantCustomTools/delete', {
      assistantId: assistantId.value,
      id: tool.id,
    });
    await fetchData();
    useAlert(t('PILOT.ASSISTANTS.CONFIG.TOOLS.REMOVE.SUCCESS'));
  } finally {
    removingId.value = null;
  }
};

// 批量移除
const handleBatchRemove = async () => {
  if (!hasSelection.value || isBatchRemoving.value) return;

  isBatchRemoving.value = true;
  try {
    const ids = Array.from(selectedToolIds.value);
    await Promise.all(
      ids.map(toolId =>
        store.dispatch('pilotAssistantCustomTools/delete', {
          assistantId: assistantId.value,
          id: toolId,
        })
      )
    );
    await fetchData();
    selectedToolIds.value = new Set();
    useAlert(t('PILOT.ASSISTANTS.CONFIG.TOOLS.BULK_ACTION.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message ||
        t('PILOT.ASSISTANTS.CONFIG.TOOLS.BULK_ACTION.DELETE_ERROR')
    );
  } finally {
    isBatchRemoving.value = false;
  }
};

// 关闭弹窗
const handleSelectorClose = () => {
  fetchData();
};

// 加载数据
const fetchData = async () => {
  if (!assistantId.value) return;
  try {
    await store.dispatch('pilotAssistantCustomTools/get', {
      assistantId: assistantId.value,
    });
  } catch (error) {
    // 忽略错误
  }
};

// 监听 assistantId 变化
watch(
  assistantId,
  newId => {
    if (newId) {
      fetchData();
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchData();
});
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 一级标题 -->
    <h2 class="text-base font-semibold text-n-slate-12 pt-4">
      {{ t('PILOT.ASSISTANTS.CONFIG.NAV.TOOLS') }}
    </h2>

    <!-- 工具配置 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('PILOT.ASSISTANTS.CONFIG.TOOLS.TITLE') }}
          </h3>
          <span
            v-tooltip="'配置助手可以使用的自定义工具'"
            class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
          />
        </div>
      </div>

      <!-- 空状态引导 -->
      <div v-if="isEmpty" class="relative overflow-hidden rounded-xl">
        <!-- 背景假卡片 -->
        <div class="space-y-2 p-4 opacity-50 pointer-events-none">
          <div
            v-for="tool in emptyStateTools"
            :key="tool.id"
            class="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
          >
            <div class="flex items-center gap-2">
              <span class="text-sm text-n-slate-12">{{ tool.title }}</span>
            </div>
          </div>
        </div>

        <!-- 渐变蒙层 + 内容 -->
        <div
          class="absolute inset-0 flex flex-col items-center justify-end pb-10 bg-gradient-to-t from-[#F3F4F7] from-30% to-transparent dark:from-n-slate-7/50 dark:to-transparent"
        >
          <h3 class="text-xl font-medium text-n-slate-12 mb-2">
            {{ t('PILOT.ASSISTANTS.CONFIG.TOOLS.EMPTY_STATE.TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-4 max-w-md text-center">
            {{ t('PILOT.ASSISTANTS.CONFIG.TOOLS.EMPTY_STATE.DESCRIPTION') }}
          </p>
          <button
            class="px-4 py-2 bg-n-brand text-white text-sm rounded-lg hover:bg-n-brand-dark transition-colors"
            @click="handleOpenSelector"
          >
            {{ t('PILOT.ASSISTANTS.CONFIG.TOOLS.EMPTY_STATE.BUTTON') }}
          </button>
        </div>
      </div>

      <!-- 已有关联工具列表 -->
      <div v-else class="flex flex-col gap-2">
        <!-- 批量操作工具条 -->
        <BulkSelectBar
          v-model="selectedToolIds"
          :all-items="linkedTools"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          :delete-label="
            t('PILOT.ASSISTANTS.CONFIG.TOOLS.BULK_ACTION.DELETE_BUTTON')
          "
          no-background
          @bulk-delete="handleBatchRemove"
        >
          <template #default-actions>
            <div class="flex justify-between items-center w-full">
              <Input
                v-model="searchQuery"
                size="sm"
                :placeholder="
                  t('PILOT.ASSISTANTS.CONFIG.TOOLS.SEARCH.PLACEHOLDER')
                "
              />
              <Button
                :label="t('PILOT.ASSISTANTS.CONFIG.TOOLS.LINK_BUTTON')"
                sm
                slate
                @click="handleOpenSelector"
              />
            </div>
          </template>
        </BulkSelectBar>

        <!-- 搜索无结果 -->
        <div v-if="filteredTools.length === 0" class="py-4 text-center">
          <span class="text-sm text-n-slate-11">{{
            t('PILOT.ASSISTANTS.CONFIG.TOOLS.SEARCH.NO_RESULTS')
          }}</span>
        </div>

        <!-- 工具列表 -->
        <div v-else class="flex flex-col gap-2">
          <div
            v-for="tool in filteredTools"
            :key="tool.id"
            class="relative flex items-center justify-between w-full gap-2 p-4 rounded-xl outline-1 outline transition-colors"
            :class="[
              isSelected(tool.id)
                ? 'outline-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                : 'outline-n-container bg-n-solid-2',
            ]"
            @mouseenter="hoveredToolId = tool.id"
            @mouseleave="hoveredToolId = null"
          >
            <!-- Checkbox 绝对定位在左上角，hover 或选中时显示 -->
            <div
              v-show="hoveredToolId === tool.id || selectedToolIds.size > 0"
              class="absolute top-4 ltr:left-3 rtl:right-3"
            >
              <Checkbox
                :model-value="isSelected(tool.id)"
                @update:model-value="toggleSelection(tool.id)"
              />
            </div>

            <!-- 左侧内容 -->
            <div class="flex flex-col min-w-0 flex-1 ltr:ml-6 rtl:mr-6">
              <span class="text-sm text-n-slate-12 font-medium truncate">{{
                tool.title
              }}</span>
              <span class="text-sm text-n-slate-11 mt-1 truncate">{{
                tool.description ||
                t('PILOT.ASSISTANTS.CONFIG.TOOLS.LIST.NO_DESCRIPTION')
              }}</span>
              <span
                v-if="tool.auth_type && tool.auth_type !== 'none'"
                class="text-sm text-n-slate-11 mt-1 inline-flex items-center gap-1"
              >
                <i class="i-lucide-lock text-base" />
                {{ getAuthTypeLabel(tool.auth_type) }}
              </span>
            </div>
            <!-- 移除按钮 - 右上角 -->
            <div
              class="absolute top-4 ltr:right-3 rtl:left-3 flex items-center gap-2"
            >
              <Button
                icon="i-lucide-trash"
                slate
                xs
                ghost
                :is-loading="removingId === tool.id"
                :disabled="removingId !== null"
                @click="handleRemoveTool(tool)"
              />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 工具选择器弹窗 -->
    <ToolSelectorDialog
      v-if="assistantId"
      ref="selectorDialog"
      :assistant-id="assistantId"
      @close="handleSelectorClose"
    />
  </div>
</template>
